/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\ParkingLotCallBehaviorSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Applications module.
----------------------------------------------------------------*/


import { ParkingCircle } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Label } from '../../../components/ui/label';
import Toggle from '../../../components/ui/Toggle';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select-shadcn';
import RecordingSelect from '../../../components/ui/RecordingSelect';
import type { ParkingLotFormState } from '../types';

interface MOHSource {
    id: number;
    name: string;
    class_name: string;
    enabled: boolean;
}

interface ParkingLotCallBehaviorSectionProps {
    formData: ParkingLotFormState;
    mohSources: MOHSource[];
    loadingMoh: boolean;
    onChange: (field: keyof ParkingLotFormState, value: ParkingLotFormState[keyof ParkingLotFormState]) => void;
    t: (key: string) => string;
}

export function ParkingLotCallBehaviorSection({ formData, mohSources, loadingMoh, onChange, t }: ParkingLotCallBehaviorSectionProps) {
    return (
        <FormBox title={t('parkingLots.sections.callBehavior')} icon={<ParkingCircle className="h-5 w-5" />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
                {/* Courtesy Tone */}
                <Label htmlFor="courtesyTone">
                    <SimpleTooltip content={t('parkingLots.fields.courtesyTone.tooltip')}>
                        {t('parkingLots.fields.courtesyTone.label')}
                    </SimpleTooltip>
                </Label>
                <div>
                    <Select value={formData.courtesyTone} onValueChange={(value) => onChange('courtesyTone', value)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="caller">{t('parkingLots.fields.courtesyTone.options.caller')}</SelectItem>
                            <SelectItem value="none">{t('parkingLots.fields.courtesyTone.options.none')}</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* MOH Class */}
                <Label htmlFor="mohClass">
                    <SimpleTooltip content={t('parkingLots.fields.mohClass.tooltip')}>
                        {t('parkingLots.fields.mohClass.label')}
                    </SimpleTooltip>
                </Label>
                <div>
                    {loadingMoh ? (
                        <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
                    ) : (
                        <select
                            id="mohClass"
                            value={formData.mohClass}
                            onChange={(e) => onChange('mohClass', e.target.value)}
                            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                            <option value="default">{t('parkingLots.fields.mohClass.options.default')}</option>
                            {mohSources.map((moh) => (
                                <option key={moh.id} value={moh.class_name}>
                                    {moh.name} ({moh.class_name})
                                </option>
                            ))}
                        </select>
                    )}
                </div>

                {/* Return to Originator */}
                <Label htmlFor="returnToOriginator">
                    <SimpleTooltip content={t('parkingLots.fields.returnToOriginator.tooltip')}>
                        {t('parkingLots.fields.returnToOriginator.label')}
                    </SimpleTooltip>
                </Label>
                <div>
                    <Toggle value={formData.returnToOriginator} onChange={(value) => onChange('returnToOriginator', value)} />
                </div>

                {/* Announce Position */}
                <Label htmlFor="announcePosition">
                    <SimpleTooltip content={t('parkingLots.fields.announcePosition.tooltip')}>
                        {t('parkingLots.fields.announcePosition.label')}
                    </SimpleTooltip>
                </Label>
                <div>
                    <Toggle value={formData.announcePosition} onChange={(value) => onChange('announcePosition', value)} />
                </div>

                {/* Call Transfer */}
                <Label htmlFor="callTransfer">
                    <SimpleTooltip content={t('parkingLots.fields.callTransfer.tooltip')}>
                        {t('parkingLots.fields.callTransfer.label')}
                    </SimpleTooltip>
                </Label>
                <div>
                    <Toggle value={formData.callTransfer} onChange={(value) => onChange('callTransfer', value)} />
                </div>

                {/* Call Reparking */}
                <Label htmlFor="callReparking">
                    <SimpleTooltip content={t('parkingLots.fields.callReparking.tooltip')}>
                        {t('parkingLots.fields.callReparking.label')}
                    </SimpleTooltip>
                </Label>
                <div>
                    <Toggle value={formData.callReparking} onChange={(value) => onChange('callReparking', value)} />
                </div>

                {/* Call Hangup */}
                <Label htmlFor="callHangup">
                    <SimpleTooltip content={t('parkingLots.fields.callHangup.tooltip')}>
                        {t('parkingLots.fields.callHangup.label')}
                    </SimpleTooltip>
                </Label>
                <div>
                    <Toggle value={formData.callHangup} onChange={(value) => onChange('callHangup', value)} />
                </div>

                {/* Record */}
                <Label htmlFor="record">
                    <SimpleTooltip content={t('parkingLots.fields.record.tooltip')}>
                        {t('parkingLots.fields.record.label')}
                    </SimpleTooltip>
                </Label>
                <div>
                    <Toggle value={formData.record} onChange={(value) => onChange('record', value)} />
                </div>

                {/* Announcement Path */}
                <Label htmlFor="announcementPath">
                    <SimpleTooltip content={t('parkingLots.fields.announcementPath.tooltip')}>
                        {t('parkingLots.fields.announcementPath.label')}
                    </SimpleTooltip>
                </Label>
                <div className="col-span-3 sm:col-span-1">
                    <RecordingSelect
                        value={formData.announcementPath || ''}
                        onChange={(value) => onChange('announcementPath', value || '')}
                        placeholder={t('parkingLots.fields.announcementPath.placeholder')}
                        returnType="id"
                        showPlayButton
                    />
                </div>

                {/* Enabled */}
                <Label htmlFor="enabled">
                    <SimpleTooltip content={t('parkingLots.fields.enabled.tooltip')}>
                        {t('parkingLots.fields.enabled.label')}
                    </SimpleTooltip>
                </Label>
                <div>
                    <Toggle value={formData.enabled} onChange={(value) => onChange('enabled', value)} />
                </div>
            </div>
        </FormBox>
    );
}
